import React, { useCallback } from 'react';
import Modal from 'utils/packages/Modal';
import { useAppDispatch } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import * as invest from '../types';
import { createOrder } from '../api/orders';
import { getOrders } from '../actions';
import OrderSendingView from '../components/OrderSendingView';
import { getLimitStopOrderExpiryDate } from '../utils/format';
import LimitStopOrderConfirmView from '../components/LimitStopOrderConfirmView';
import LimitBuyOrderConfirmView from '../components/LimitBuyOrderConfirmView';
const strings = {
    failedToExecute: 'Failed to execute the order.',
};
const useLimitStopOrderConfirm = (symbol, tradingAccountId, assetId, operation) => {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const onSecondaryButtonPress = useCallback(() => {
        navigation.navigate('InvestSelectAccountToTopup', { flow: 'recurringOrder' });
        return true;
    }, [navigation]);
    const executeCallback = useCallback(async ({ pin, amount, shares, expiryDate, orderType }) => {
        if (tradingAccountId) {
            const content = <OrderSendingView />;
            Modal.hide();
            Modal.show(content, {
                top: true,
                fullscreen: true,
                nonDismissable: true,
                minShowTime: 1500,
            });
            try {
                const result = await createOrder(shares, assetId, tradingAccountId, operation, pin, orderType, orderType === invest.SellOrderType.LIMIT || orderType === invest.OrderType.LIMIT ? amount.value : undefined, orderType === invest.SellOrderType.STOP ? amount.value : undefined, 
                // eslint-disable-next-line no-nested-ternary
                expiryDate
                    ? expiryDate.type === invest.LimitStopOrderExpiryDateType.TODAY
                        ? undefined
                        : getLimitStopOrderExpiryDate(expiryDate)
                    : undefined);
                if (result) {
                    const params = {
                        accountId: tradingAccountId,
                        statuses: ['queued', 'pending'],
                        orderTypes: [invest.SellOrderType.LIMIT, invest.SellOrderType.STOP],
                    };
                    // Refreshing only limit and sell orders for the main list as well as for the symbol
                    dispatch(getOrders(params));
                    if (assetId) {
                        dispatch(getOrders({ ...params, assetIds: [assetId] }));
                    }
                    Modal.hide(() => {
                        navigation.navigateDeprecated('Success', {
                            onSecondaryButtonPress,
                            title: 'Order created',
                            subTitle: `Your order has been created.`,
                            buttonText: 'Done',
                            onContinue: () => {
                                const { routes } = navigation.getState();
                                const stockScreen = routes.find((route) => route.name === 'InvestStock');
                                if (stockScreen) {
                                    navigation.navigateDeprecated('InvestStock', {
                                        assetId,
                                    });
                                }
                                else {
                                    navigation.popToTop();
                                }
                                return true;
                            },
                        });
                    }, { top: true });
                }
                else {
                    throw new Error();
                }
            }
            catch (error) {
                const axiosErr = error;
                let message = strings.failedToExecute;
                if (axiosErr?.response?.data?.user_message) {
                    message = axiosErr.response.data.user_message;
                }
                else if ('message' in error && error.message) {
                    message = error.message;
                }
                // failed to execute an order
                if (error?.errorType === 'PinError') {
                    Modal.hide(undefined, { top: true });
                    throw new Error('Refresh token expired');
                }
                else {
                    Modal.hide(() => {
                        Modal.showError(message);
                    }, { top: true });
                }
            }
        }
    }, [assetId, onSecondaryButtonPress, operation, tradingAccountId]);
    const confirmCallback = useCallback((amount, shares, expiryDate, orderType, latestPrice, latestPriceTimeStamp, fxConversion, totalAmount, priceConvertedToUserCurrency) => {
        const content = operation === 'buy' ? (<LimitBuyOrderConfirmView symbol={symbol} shares={shares} assetId={assetId} execute={executeCallback} fxConversion={fxConversion}/>) : (<LimitStopOrderConfirmView symbol={symbol} amount={amount} shares={shares} assetId={assetId} orderType={orderType} expiryDate={expiryDate} execute={executeCallback} latestPrice={latestPrice} totalAmount={totalAmount} fxConversion={fxConversion} latestPriceTimeStamp={latestPriceTimeStamp} priceConvertedToUserCurrency={priceConvertedToUserCurrency}/>);
        Modal.show(content);
    }, [assetId, executeCallback, operation, symbol]);
    return [confirmCallback];
};
export default useLimitStopOrderConfirm;
